import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SocketService } from '../socket/socket.service';

export interface LlamadaEntrante {
    wacid: string;
    telefono: string;
    nombre: string | null;
    conversationId: number | null;
    /** Oferta SDP del cliente, tal cual la manda Meta. */
    sdp: string | null;
    /** ¿Se graba? Lo decide la línea en el servidor, no el navegador. */
    grabar?: boolean;
}

export type EstadoLlamada = 'inactiva' | 'sonando' | 'conectando' | 'activa' | 'llamando';

/** `desconocido` = el navegador no deja consultarlo sin preguntar. */
export type EstadoMicrofono = 'granted' | 'denied' | 'prompt' | 'desconocido';

/** En qué quedó el intento de llamar: se llamó, se pidió permiso, o no se puede. */
export interface ResultadoInicio {
    estado: 'llamando' | 'permiso_pedido' | 'sin_permiso';
    motivo?: string;
}

export interface CalidadLlamada {
    /** Milisegundos de ida y vuelta hasta el otro extremo. */
    latenciaMs: number | null;
    /** Porcentaje de paquetes de audio perdidos. Lo que se oye como cortes. */
    perdidaPct: number | null;
    /** Variación del retardo, en ms. Alta = voz entrecortada aunque no se pierda nada. */
    jitterMs: number | null;
    /** true si el audio va por el TURN en vez de directo. */
    porRelay: boolean;
    nivel: 'buena' | 'regular' | 'mala';
}

export interface EstadoPermiso {
    puedeLlamar: boolean;
    estado: 'NONE' | 'TEMPORARY' | 'PERMANENT' | 'DENIED';
    caducaEl: string | null;
    puedePedir: boolean;
    motivo: string | null;
}

/**
 * Cuánto se espera como mucho a que el navegador termine de reunir candidatos
 * ICE. Meta NO acepta trickle: el SDP que le mandamos tiene que llevar ya todos
 * los candidatos dentro. Pero esperar a `complete` sin límite es arriesgado —un
 * TURN que no responde puede colgarse hasta 20 s— y el presupuesto total antes
 * de que Meta corte la llamada es de apenas unos segundos. Se manda lo que haya
 * reunido al vencer el plazo: con el host y el STUN suele bastar.
 */
const MAX_ESPERA_ICE_MS = 3000;

/**
 * Llamadas de WhatsApp en el navegador del agente.
 *
 * El audio NO pasa por el backend: el peer WebRTC es esta pestaña y el servidor
 * solo hace de cartero de SDPs hasta Meta. La consecuencia práctica es que la
 * llamada vive y muere con la pestaña — si el agente la cierra, se corta.
 */
@Injectable({ providedIn: 'root' })
export class CallService {
    private readonly api = environment.apiUrl;
    private http = inject(HttpClient);
    private socket = inject(SocketService);

    private _estado = signal<EstadoLlamada>('inactiva');
    private _llamada = signal<LlamadaEntrante | null>(null);
    private _error = signal<string | null>(null);
    private _segundos = signal(0);
    private _silenciado = signal(false);
    private _timbreApagado = signal(false);
    private _minimizada = signal(false);
    private _calidad = signal<CalidadLlamada | null>(null);
    private _microfono = signal<EstadoMicrofono>('desconocido');

    /** Estado de la llamada en curso, para que el modal reaccione. */
    estado = computed(() => this._estado());
    llamada = computed(() => this._llamada());
    error = computed(() => this._error());
    /** Segundos transcurridos desde que se contestó. */
    segundos = computed(() => this._segundos());
    /** ¿Está silenciado el micrófono del agente? */
    silenciado = computed(() => this._silenciado());
    /** El agente apagó el timbre de esta llamada, pero sigue sonando para él. */
    timbreApagado = computed(() => this._timbreApagado());
    /** Modal apartado a un lado; la llamada sigue viva. */
    minimizada = computed(() => this._minimizada());
    /** Estado de la red durante la llamada. Null hasta que hay datos. */
    calidad = computed(() => this._calidad());
    /** Permiso de micrófono, consultado sin mostrar diálogo. */
    microfono = computed(() => this._microfono());

    /**
     * Servidores ICE con las credenciales temporales del backend, y el momento
     * en que caducan. Se piden una vez y se reutilizan mientras valgan: pedirlas
     * al entrar la llamada gastaría parte de los segundos que da Meta para
     * contestar.
     */
    private ice: { servers: RTCIceServer[]; caducaEn: number; conTurn: boolean } | null = null;

    private pc: RTCPeerConnection | null = null;
    private micLocal: MediaStream | null = null;
    private cronometro: any = null;
    private muestreo: any = null;
    private grabadora: MediaRecorder | null = null;
    private ctxGrabacion: AudioContext | null = null;
    private segundosGrabados = 0;
    private iniciado = false;

    /** Elemento donde suena el audio del cliente. Lo registra el modal. */
    private audioRemoto: HTMLAudioElement | null = null;

    /**
     * Se llama una vez tras el login. Suscribe los eventos y deja el permiso de
     * micrófono ya concedido: si se pidiera al entrar la llamada, el diálogo del
     * navegador se comería buena parte de los segundos que da Meta para
     * contestar, y con el agente mirando un popup en vez del modal.
     */
    iniciar(): void {
        if (this.iniciado) return;
        this.iniciado = true;

        this.socket.onIncomingCall().subscribe((datos) => {
            // Si ya hay una llamada encima, esta no se pinta: el backend no
            // debería ofrecérnosla, pero una carrera es posible y dos modales a
            // la vez serían peor que perder la segunda.
            if (this._estado() !== 'inactiva') return;
            this._error.set(null);
            this._llamada.set(datos);
            this._estado.set('sonando');
        });

        this.socket.onCallAnsweredElsewhere().subscribe(({ wacid }) => {
            if (this._llamada()?.wacid === wacid) this.terminar();
        });

        this.socket.onCallEnded().subscribe(({ wacid }) => {
            if (this._llamada()?.wacid === wacid) this.terminar();
        });

        // Saliente: el cliente descolgó. Su SDP es lo último que falta para que
        // empiece a fluir el audio.
        this.socket.onCallAnswer().subscribe(({ wacid, sdp }) => {
            if (this._llamada()?.wacid !== wacid || !this.pc) return;
            this.pc
                .setRemoteDescription({ type: 'answer', sdp })
                .then(() => {
                    this._estado.set('activa');
                    this.arrancarCronometro();
                })
                .catch((e) => {
                    console.error('[Calls] respuesta del cliente rechazada por el navegador', e);
                    this._error.set('No se pudo establecer el audio');
                    this.terminar();
                });
        });

        // Ojo: aquí NO se pide el micrófono. Ver `comprobarMicrofono()`.
        void this.comprobarMicrofono();
        void this.cargarIce();
    }

    /**
     * Trae los servidores ICE del backend. Las credenciales de TURN caducan, y
     * llegan firmadas para este agente: por eso no pueden estar en el bundle.
     *
     * Si falla, se sigue con lo que haya en `environment` para no dejar al
     * agente sin poder contestar por un problema de red pasajero.
     */
    private async cargarIce(): Promise<RTCIceServer[]> {
        const vigente = this.ice && this.ice.caducaEn > Date.now() + 60_000;
        if (vigente) return this.ice!.servers;

        try {
            const r = await firstValueFrom(
                this.http.get<{ iceServers: RTCIceServer[]; expiraEn: number; conTurn: boolean }>(
                    `${this.api}/calls/ice`,
                ),
            );
            this.ice = {
                servers: r.iceServers,
                caducaEn: Date.now() + r.expiraEn * 1000,
                conTurn: r.conTurn,
            };
            if (!r.conTurn) {
                console.warn(
                    '[Calls] sin TURN configurado en el servidor. Si estás tras el firewall de ' +
                    'una oficina, las llamadas se conectarán pero no oirás nada.',
                );
            }
            return r.iceServers;
        } catch {
            console.warn('[Calls] no se pudo obtener la config ICE; se usa la del entorno');
            return environment.iceServers;
        }
    }

    /** ¿El servidor tiene TURN? El modal lo usa para avisar antes de fallar. */
    get hayTurn(): boolean {
        return this.ice?.conTurn ?? true;
    }

    /** El modal registra su <audio> para que le enganchemos la pista remota. */
    registrarAudio(el: HTMLAudioElement): void {
        this.audioRemoto = el;
    }

    /**
     * Consulta el estado del micrófono SIN mostrar ningún diálogo.
     *
     * Antes se pedía el permiso nada más iniciar sesión, y el resultado era el
     * contrario del buscado: al agente le saltaba el diálogo del navegador
     * mientras escribía en un chat, sin ninguna llamada de por medio. Fuera de
     * contexto, lo natural es pulsar "Bloquear" — y con eso queda incapaz de
     * atender llamadas hasta que alguien le explique cómo revertirlo.
     *
     * Así que solo se mira, y pedirlo queda para cuando el agente lo decida o
     * cuando conteste una llamada, que es cuando el diálogo se entiende.
     */
    async comprobarMicrofono(): Promise<EstadoMicrofono> {
        try {
            const p = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            this._microfono.set(p.state as EstadoMicrofono);
            // El estado puede cambiar desde la barra del navegador sin recargar.
            p.onchange = () => this._microfono.set(p.state as EstadoMicrofono);
            return p.state as EstadoMicrofono;
        } catch {
            // Navegador sin Permissions API para micrófono: no se puede saber
            // sin preguntar, y preguntar es justo lo que se quiere evitar.
            this._microfono.set('desconocido');
            return 'desconocido';
        }
    }

    /**
     * Pide el permiso de micrófono. Se llama desde un BOTÓN, nunca solo.
     *
     * Suelta la pista enseguida: mantener el stream abierto dejaría encendido
     * el indicador de "grabando" en la pestaña todo el día. Con el permiso ya
     * concedido, el `getUserMedia` de verdad —al contestar— no vuelve a
     * preguntar y resuelve en milisegundos, que es lo que salva la ventana de
     * respuesta de Meta.
     */
    async pedirPermisoMicrofono(): Promise<boolean> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            for (const pista of stream.getTracks()) pista.stop();
            this._microfono.set('granted');
            return true;
        } catch {
            this._microfono.set('denied');
            console.warn('[Calls] permiso de micrófono no concedido; no se podrán contestar llamadas');
            return false;
        }
    }

    /** Contestar: se monta el peer, se genera el SDP y se manda al backend. */
    async contestar(): Promise<void> {
        const llamada = this._llamada();
        if (!llamada || this._estado() !== 'sonando') return;
        if (!llamada.sdp) {
            this._error.set('La llamada llegó sin oferta de audio');
            return;
        }

        this._estado.set('conectando');
        try {
            const sdp = await this.prepararRespuesta(llamada.sdp);
            await firstValueFrom(
                this.http.post(`${this.api}/calls/${encodeURIComponent(llamada.wacid)}/answer`, { sdp }),
            );
            this._estado.set('activa');
            this.arrancarCronometro();
        } catch (e: any) {
            // 409 = otro agente (u otra pestaña) la cogió primero. No es un error
            // que merezca alarma: el modal simplemente se cierra.
            if (e?.status !== 409) {
                this._error.set('No se pudo contestar la llamada');
                console.error('[Calls] fallo al contestar', e);
            }
            this.terminar();
        }
    }

    /** ¿Podemos llamar a este cliente? Lo pregunta el chat para pintar el botón. */
    async permiso(conversationId: number): Promise<EstadoPermiso | null> {
        try {
            return await firstValueFrom(
                this.http.get<EstadoPermiso>(`${this.api}/calls/permission/${conversationId}`),
            );
        } catch {
            return null;
        }
    }

    /**
     * Pide permiso al cliente. Gasta cupo: 1 cada 24 h, 2 por semana.
     *
     * Devuelve `yaTenia` cuando Meta responde que el cliente ya había
     * autorizado: en ese caso no se manda nada, el CRM se pone al día solo y se
     * puede llamar de inmediato.
     */
    async pedirPermiso(conversationId: number): Promise<{ ok: boolean; yaTenia: boolean }> {
        try {
            const r = await firstValueFrom(
                this.http.post<{ solicitado: boolean; yaTenia?: boolean }>(
                    `${this.api}/calls/permission/${conversationId}/request`,
                    {},
                ),
            );
            return { ok: true, yaTenia: r?.yaTenia === true };
        } catch (e: any) {
            this._error.set(e?.error?.message ?? 'No se pudo pedir el permiso');
            return { ok: false, yaTenia: false };
        }
    }

    /**
     * Llamar a un cliente resolviendo antes su permiso.
     *
     * Es el camino completo que sigue el agente al pulsar "Llamar": comprobar
     * si el cliente autorizó, pedírselo si no, y llamar en cuanto se pueda.
     * Vive aqui y no en un componente porque hay dos puertas de entrada —el
     * botón del panel y el enlace que llega desde Zoho— y si cada una llevara
     * su copia, arreglar el flujo en una dejaría la otra atrás.
     */
    async iniciarDesdeConversacion(
        conversationId: number,
        nombre: string | null,
        telefono: string,
    ): Promise<ResultadoInicio> {
        const permiso = await this.permiso(conversationId);

        if (permiso?.puedeLlamar) {
            await this.llamar(conversationId, nombre, telefono);
            return { estado: 'llamando' };
        }

        if (!permiso?.puedePedir) {
            return { estado: 'sin_permiso', motivo: permiso?.motivo ?? 'El cliente no autorizó recibir llamadas.' };
        }

        const r = await this.pedirPermiso(conversationId);
        if (!r.ok) return { estado: 'sin_permiso', motivo: this.error() ?? 'No se pudo pedir el permiso' };

        // Meta nos corrigió: el cliente ya tenía permiso. Se llama sin obligar
        // a pulsar otra vez, que es lo que el agente iba a hacer de todos modos.
        if (r.yaTenia) {
            const actualizado = await this.permiso(conversationId);
            if (actualizado?.puedeLlamar) {
                await this.llamar(conversationId, nombre, telefono);
                return { estado: 'llamando' };
            }
        }

        return { estado: 'permiso_pedido' };
    }

    /**
     * Llamar al cliente.
     *
     * Aquí el navegador genera la OFERTA —al revés que en una entrante— y el
     * cliente responde cuando descuelga, por el socket. Mientras tanto el
     * agente oye el tono de espera.
     */
    async llamar(conversationId: number, nombre: string | null, telefono: string): Promise<void> {
        if (this._estado() !== 'inactiva') return;

        this._error.set(null);
        this._llamada.set({ wacid: '', telefono, nombre, conversationId, sdp: null });
        this._estado.set('conectando');

        try {
            const sdp = await this.prepararOferta();
            const r = await firstValueFrom(
                this.http.post<{ wacid: string; grabar?: boolean }>(
                    `${this.api}/calls/outbound`, { conversationId, sdp },
                ),
            );
            // `grabar` llega aquí y no antes porque lo decide el servidor según
            // la línea. Llega a tiempo: la grabación arranca en `ontrack`, que
            // no ocurre hasta que el cliente descuelga.
            this._llamada.update((l) => (l ? { ...l, wacid: r.wacid, grabar: r.grabar === true } : l));
            // `llamando` = ya salió, esperando que descuelgue. El modal cambia
            // el timbre por el tono de espera.
            this._estado.set('llamando');
        } catch (e: any) {
            this._error.set(e?.error?.message ?? 'No se pudo iniciar la llamada');
            this.terminar();
        }
    }

    async rechazar(): Promise<void> {
        const llamada = this._llamada();
        if (!llamada) return;
        try {
            await firstValueFrom(
                this.http.post(`${this.api}/calls/${encodeURIComponent(llamada.wacid)}/reject`, {}),
            );
        } catch (e) {
            console.warn('[Calls] fallo al rechazar', e);
        }
        this.terminar();
    }

    async colgar(): Promise<void> {
        const llamada = this._llamada();
        if (!llamada) return;
        try {
            await firstValueFrom(
                this.http.post(`${this.api}/calls/${encodeURIComponent(llamada.wacid)}/hangup`, {}),
            );
        } catch (e) {
            console.warn('[Calls] fallo al colgar', e);
        }
        this.terminar();
    }

    /** Silencia o reactiva el micrófono sin tocar la conexión. */
    alternarMicrofono(): boolean {
        const pista = this.micLocal?.getAudioTracks()[0];
        if (!pista) return false;
        pista.enabled = !pista.enabled;
        this._silenciado.set(!pista.enabled);
        return !pista.enabled;
    }

    /**
     * Apaga el timbre de ESTA llamada sin rechazarla.
     *
     * Para cuando el agente no puede cogerla ahora mismo pero no quiere
     * negársela al cliente: rechazarla la da por perdida y corta el intento,
     * mientras que callar el timbre la deja sonando su ciclo normal, por si él
     * mismo se libera o la coge un compañero.
     */
    silenciarTimbre(): void {
        this._timbreApagado.set(true);
    }

    /** Aparta el modal a un lado. La llamada sigue viva y se puede recuperar. */
    minimizar(): void {
        this._minimizada.set(true);
        this._timbreApagado.set(true);
    }

    restaurar(): void {
        this._minimizada.set(false);
    }

    // ── Interno ─────────────────────────────────────────────────────────────

    /** Monta el RTCPeerConnection y devuelve el SDP de respuesta ya completo. */
    private async prepararRespuesta(ofertaSdp: string): Promise<string> {
        this.micLocal = await navigator.mediaDevices.getUserMedia({ audio: true });

        const pc = new RTCPeerConnection({ iceServers: await this.cargarIce() });
        this.pc = pc;

        for (const pista of this.micLocal.getTracks()) {
            pc.addTrack(pista, this.micLocal);
        }

        pc.ontrack = (ev) => {
            if (this.audioRemoto && ev.streams[0]) {
                this.audioRemoto.srcObject = ev.streams[0];
                void this.audioRemoto.play().catch(() => undefined);
                // Aquí es donde por fin existen las DOS voces en el navegador:
                // es el único momento y lugar en que se puede grabar la llamada.
                this.iniciarGrabacion(ev.streams[0]);
            }
        };

        pc.onconnectionstatechange = () => {
            if (['failed', 'closed'].includes(pc.connectionState)) {
                this._error.set('Se perdió la conexión de audio');
                this.terminar();
            }
        };

        await pc.setRemoteDescription({ type: 'offer', sdp: ofertaSdp });
        const respuesta = await pc.createAnswer();
        await pc.setLocalDescription(respuesta);
        await this.esperarCandidatos(pc);

        return pc.localDescription?.sdp ?? respuesta.sdp ?? '';
    }

    /**
     * Monta el peer para una llamada NUESTRA y devuelve la oferta.
     *
     * Igual que `prepararRespuesta` pero al revés: aquí no hay oferta previa que
     * responder, la creamos nosotros y el cliente contestará al descolgar.
     */
    private async prepararOferta(): Promise<string> {
        this.micLocal = await navigator.mediaDevices.getUserMedia({ audio: true });

        const pc = new RTCPeerConnection({ iceServers: await this.cargarIce() });
        this.pc = pc;

        for (const pista of this.micLocal.getTracks()) pc.addTrack(pista, this.micLocal);

        pc.ontrack = (ev) => {
            if (this.audioRemoto && ev.streams[0]) {
                this.audioRemoto.srcObject = ev.streams[0];
                void this.audioRemoto.play().catch(() => undefined);
                // Aquí es donde por fin existen las DOS voces en el navegador:
                // es el único momento y lugar en que se puede grabar la llamada.
                this.iniciarGrabacion(ev.streams[0]);
            }
        };
        pc.onconnectionstatechange = () => {
            if (['failed', 'closed'].includes(pc.connectionState)) {
                this._error.set('Se perdió la conexión de audio');
                this.terminar();
            }
        };

        const oferta = await pc.createOffer({ offerToReceiveAudio: true });
        await pc.setLocalDescription(oferta);
        await this.esperarCandidatos(pc);

        return pc.localDescription?.sdp ?? oferta.sdp ?? '';
    }

    /** Espera a que el SDP local lleve todos los candidatos (ver MAX_ESPERA_ICE_MS). */
    private esperarCandidatos(pc: RTCPeerConnection): Promise<void> {
        if (pc.iceGatheringState === 'complete') return Promise.resolve();

        return new Promise<void>((resolve) => {
            const acabar = () => {
                clearTimeout(plazo);
                pc.removeEventListener('icegatheringstatechange', comprobar);
                resolve();
            };
            const comprobar = () => {
                if (pc.iceGatheringState === 'complete') acabar();
            };
            const plazo = setTimeout(acabar, MAX_ESPERA_ICE_MS);
            pc.addEventListener('icegatheringstatechange', comprobar);
        });
    }

    private arrancarCronometro(): void {
        this._segundos.set(0);
        this.cronometro = setInterval(() => this._segundos.update((s) => s + 1), 1000);
        this.arrancarMuestreo();
    }

    /**
     * Graba la llamada en ESTÉREO: el agente en un canal, el cliente en el otro.
     *
     * Separar las voces no cuesta nada al grabar y es imposible de recuperar
     * después: con todo mezclado en mono, saber quién dijo qué pasa a ser un
     * problema difícil. Con un canal para cada uno, la transcripción por
     * interlocutores es trivial.
     *
     * Se envía en trozos de 10 s en vez de un archivo al final: si el agente
     * cierra la pestaña o el navegador se cae, se conserva todo menos el último
     * fragmento en lugar de perder la conversación entera.
     */
    private iniciarGrabacion(remoto: MediaStream): void {
        if (this.grabadora || !this.micLocal || !this._llamada()?.grabar) return;

        try {
            const ctx = new AudioContext();
            this.ctxGrabacion = ctx;

            const mezcla = ctx.createChannelMerger(2);
            ctx.createMediaStreamSource(this.micLocal).connect(mezcla, 0, 0); // izquierda: agente
            ctx.createMediaStreamSource(remoto).connect(mezcla, 0, 1);        // derecha: cliente

            const destino = ctx.createMediaStreamDestination();
            mezcla.connect(destino);

            const grabadora = new MediaRecorder(destino.stream, {
                mimeType: 'audio/webm;codecs=opus',
                // 64 kbps para el ESTÉREO, o sea 32 por voz. Con los 32 kbps
                // anteriores cada canal se quedaba en 16, y sobre un audio que
                // ya viene de WhatsApp en banda estrecha eso castiga mucho a la
                // transcripción. Siguen siendo ~4 MB por hora: barato.
                audioBitsPerSecond: 64000,
            });
            this.grabadora = grabadora;
            this.segundosGrabados = 0;

            grabadora.ondataavailable = (ev) => {
                if (ev.data.size === 0) return;
                this.segundosGrabados += 10;
                void this.enviarTrozo(ev.data);
            };

            grabadora.start(10_000);
        } catch (e) {
            // Que no se pueda grabar nunca debe impedir hablar.
            console.warn('[Calls] no se pudo iniciar la grabación', e);
            this.grabadora = null;
        }
    }

    private async enviarTrozo(trozo: Blob): Promise<void> {
        const wacid = this._llamada()?.wacid;
        if (!wacid) return;
        try {
            await firstValueFrom(
                this.http.post(
                    `${this.api}/calls/${encodeURIComponent(wacid)}/recording/chunk`,
                    trozo,
                    { headers: { 'Content-Type': 'application/octet-stream' } },
                ),
            );
        } catch {
            // Un trozo perdido deja un hueco en el audio, no rompe la llamada.
        }
    }

    private detenerGrabacion(): void {
        const wacid = this._llamada()?.wacid;
        if (this.grabadora) {
            try {
                // `stop()` provoca un último ondataavailable con lo pendiente.
                this.grabadora.stop();
            } catch { /* ya estaba parada */ }
            this.grabadora = null;
        }
        void this.ctxGrabacion?.close().catch(() => undefined);
        this.ctxGrabacion = null;

        if (!wacid) return;
        const segundos = this.segundosGrabados;
        // Con margen para que el último trozo llegue antes que el cierre: el
        // servidor sube lo acumulado en cuanto recibe este aviso.
        setTimeout(() => {
            void firstValueFrom(
                this.http.post(`${this.api}/calls/${encodeURIComponent(wacid)}/recording/finish`, { segundos }),
            ).catch(() => undefined);
        }, 1200);
    }

    /**
     * Mide la red cada 3 s mientras dura la llamada.
     *
     * Sirve para responder en caliente la pregunta que siempre surge cuando se
     * oye mal: ¿es mi conexión, la del cliente, o el servidor? Sin esto solo
     * queda la impresión de cada uno, y no hay forma de saberlo después.
     */
    private arrancarMuestreo(): void {
        if (this.muestreo) clearInterval(this.muestreo);
        this.muestreo = setInterval(() => void this.medir(), 3000);
        void this.medir();
    }

    private async medir(): Promise<void> {
        if (!this.pc) return;
        try {
            const stats = await this.pc.getStats();
            let latenciaMs: number | null = null;
            let perdidaPct: number | null = null;
            let jitterMs: number | null = null;
            let porRelay = false;

            stats.forEach((r: any) => {
                if (r.type === 'candidate-pair' && r.state === 'succeeded' && r.nominated !== false) {
                    if (typeof r.currentRoundTripTime === 'number') {
                        latenciaMs = Math.round(r.currentRoundTripTime * 1000);
                    }
                }
                if (r.type === 'local-candidate' && r.candidateType === 'relay') porRelay = true;
                if (r.type === 'inbound-rtp' && r.kind === 'audio') {
                    if (typeof r.jitter === 'number') jitterMs = Math.round(r.jitter * 1000);
                    const recibidos = r.packetsReceived ?? 0;
                    const perdidos = r.packetsLost ?? 0;
                    const total = recibidos + perdidos;
                    if (total > 0) perdidaPct = Math.round((perdidos / total) * 1000) / 10;
                }
            });

            this._calidad.set({
                latenciaMs,
                perdidaPct,
                jitterMs,
                porRelay,
                nivel: this.nivelDe(latenciaMs, perdidaPct, jitterMs),
            });
        } catch {
            // Medir nunca debe estropear una llamada en curso.
        }
    }

    /**
     * Umbrales de voz, no de vídeo: en audio un 3 % de pérdida ya se nota como
     * cortes, y por encima de 400 ms de ida y vuelta la gente empieza a
     * pisarse al hablar.
     */
    private nivelDe(rtt: number | null, perdida: number | null, jitter: number | null): 'buena' | 'regular' | 'mala' {
        if ((perdida ?? 0) > 8 || (rtt ?? 0) > 600 || (jitter ?? 0) > 100) return 'mala';
        if ((perdida ?? 0) > 3 || (rtt ?? 0) > 300 || (jitter ?? 0) > 50) return 'regular';
        return 'buena';
    }

    /** Cierra todo y vuelve al estado inicial. Es idempotente. */
    private terminar(): void {
        // Antes de soltar el micrófono y el peer, que aún hacen falta.
        this.detenerGrabacion();
        if (this.cronometro) {
            clearInterval(this.cronometro);
            this.cronometro = null;
        }
        if (this.muestreo) {
            clearInterval(this.muestreo);
            this.muestreo = null;
        }
        for (const pista of this.micLocal?.getTracks() ?? []) pista.stop();
        this.micLocal = null;

        if (this.pc) {
            this.pc.onconnectionstatechange = null;
            this.pc.ontrack = null;
            this.pc.close();
            this.pc = null;
        }
        if (this.audioRemoto) this.audioRemoto.srcObject = null;

        this._llamada.set(null);
        this._estado.set('inactiva');
        this._segundos.set(0);
        this._silenciado.set(false);
        this._timbreApagado.set(false);
        this._minimizada.set(false);
        this._calidad.set(null);
    }
}
